import type { ReactElement } from 'react';
import { Link } from 'react-router';
import {
  MY_DESIGNS_PATH,
  MY_ORDERS_PATH,
  MY_PAGE_PATH,
  MY_PROFILE_PATH,
  MY_QUOTES_PATH,
  MY_REVIEWS_PATH,
} from '@/shared/config';
import { cn } from '@/shared/ui';

/** 마이페이지 메뉴 — 백엔드가 있는 화면만 둔다(내 주문은 주문 모듈의 화면, 주문 설계 §8-2). */
const MY_PAGE_MENU = [
  { id: 'home', label: '마이페이지 홈', path: MY_PAGE_PATH },
  { id: 'quotes', label: '내 견적', path: MY_QUOTES_PATH },
  { id: 'orders', label: '내 주문', path: MY_ORDERS_PATH },
  { id: 'reviews', label: '내 후기', path: MY_REVIEWS_PATH },
  { id: 'designs', label: '내 디자인', path: MY_DESIGNS_PATH },
  { id: 'profile', label: '회원정보', path: MY_PROFILE_PATH },
] as const;

export type MyPageMenuId = (typeof MY_PAGE_MENU)[number]['id'];

interface MyPageNavProps {
  readonly activeId: MyPageMenuId;
}

/**
 * 마이페이지 메뉴 — 넓은 화면은 왼쪽 세로 목록(현재 메뉴 앞에 라임 선), 좁은 화면은 기획전 탭과 같은 밑줄 탭(넘치면 가로로 민다).
 */
export const MyPageNav = ({ activeId }: MyPageNavProps): ReactElement => (
  <nav aria-label="마이페이지 메뉴">
    <p
      aria-hidden
      className="hidden pb-4 text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase lg:block"
    >
      Menu
    </p>
    <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
      <ul className="flex w-max min-w-full gap-7 border-b border-foreground/10 lg:w-auto lg:flex-col lg:gap-0 lg:border-t lg:border-b-0 lg:border-foreground">
        {MY_PAGE_MENU.map((item) => {
          const isActive = item.id === activeId;

          return (
            <li
              key={item.id}
              className="-mb-px shrink-0 lg:mb-0 lg:border-b lg:border-foreground/10"
            >
              <Link
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex h-12 items-center gap-3 border-b-2 text-[15px] whitespace-nowrap transition-colors lg:h-14 lg:border-b-0',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'border-foreground font-bold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'hidden h-px w-4 lg:block',
                    isActive ? 'bg-brand-lime' : 'bg-transparent',
                  )}
                />
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  </nav>
);
